/**
 * @module modules/utils
 * @description General utility functions for the application including currency formatting,
 * object manipulation, storage availability checks, data transformation, and function composition.
 */

import { nowEpoch } from 'src/utils';

/**
 * @const usdFormatter
 * @private
 * @description Intl.NumberFormat instance for formatting currency with dollars and cents ($xx.yy)
 * @type {Intl.NumberFormat}
 */
const usdFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

/**
 * @const usdFormatterShort
 * @private
 * @description Intl.NumberFormat instance for formatting currency with optional cents ($xx or $xx.yy)
 * @type {Intl.NumberFormat}
 */
const usdFormatterShort = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
});

/**
 * @function formatUsd
 * @description Format a number as USD currency with dollars and cents
 * @param {string|number} x - Amount to format
 * @returns {string} Formatted currency string (e.g., "$12.34")
 */
export const formatUsd = (x) => usdFormatter.format(x);

/**
 * @function formatUsdShort
 * @description Format a number as USD currency, showing cents only if necessary
 * @param {string|number} x - Amount to format
 * @returns {string} Formatted currency string (e.g., "$12" or "$12.34")
 */
export const formatUsdShort = (x) => {
  const noCents = (100 * x) % 100 === 0;
  return (noCents ? usdFormatterShort : usdFormatter).format(x);
};

/**
 * @const isDev
 * @description Flag indicating if running in development environment
 * @type {boolean}
 */
export const isDev = process.env.NODE_ENV === 'development';

/**
 * @func get
 * @summary This tiny/quick alternative to Lodash's get method has one limitation:
 * it only supports dot notation.
 *
 * So treat arrays like objects, instead of 👎'arr[0]', use 'arr.0'👍
 *
 * @param  {object} object       the object in which you're looking for the value
 * @param  {string} path         the dot notation path to the value
 * @param  {mixed}  defaultValue if this function resolves to 'undefined'
 *                               this value is returned instead
 *
 * @return {mixed}               the value at the path, or defaultValue
 */
export function get(_object, path, defaultValue) {
  let object = _object;
  if (!object) {
    return defaultValue;
  }

  const parts = path.split('.');

  for (let i = 0; i < parts.length; i++) {
    object = object[parts[i]];

    if (typeof object === 'undefined') {
      return defaultValue;
    }
  }

  return object;
}

/**
 * @func safeParse
 * @param {string|JSON} str JSON string (hopefully)
 * @param {*} defaultValue fallback value of invalid JSON
 * @returns {object} JSON object or `defaultValue`
 */
export function safeParse(str, defaultValue = {}) {
  try {
    return JSON.parse(str);
  } catch (e) {
    return defaultValue;
  }
}

let isAvailableLocal = null;
let isAvailableSession = null;

/**
 * @function isLocalStorageAvailable
 * @description Check if localStorage is available and writable in the current environment.
 * Caches result to avoid repeated checks. Handles browsers with disabled storage or private browsing.
 * @returns {boolean} True if localStorage is available and writable
 */
export function isLocalStorageAvailable() {
  if (isAvailableLocal === null) {
    try {
      const uid = nowEpoch().toString();
      localStorage.setItem(uid, uid);
      isAvailableLocal = localStorage.getItem(uid) === uid;
      localStorage.removeItem(uid);
    } catch (exception) {
      isAvailableLocal = false;
    }
  }
  return isAvailableLocal;
}

/**
 * @function isSessionStorageAvailable
 * @description Check if sessionStorage is available and writable in the current environment.
 * Caches result to avoid repeated checks. Handles browsers with disabled storage or private browsing.
 * @returns {boolean} True if sessionStorage is available and writable
 */
export function isSessionStorageAvailable() {
  if (isAvailableSession === null) {
    try {
      const uid = nowEpoch().toString();
      sessionStorage.setItem(uid, uid);
      isAvailableSession = sessionStorage.getItem(uid) === uid;
      sessionStorage.removeItem(uid);
    } catch (exception) {
      isAvailableSession = false;
    }
  }
  return isAvailableSession;
}

/**
 * @func groupBy
 * @summary group given collection by property
 *  - usage: const obj = groupBy(list, "group");
 *  -> will create a nested array of groups
 * @param {Array<Record<string, any>>} collection things to group
 * @param {string} property property to group by
 * @returns {Array<any>} array with grouped values
 */
export function groupBy(collection, property) {
  let val;
  let index;

  const values = [];
  const result = [];

  for (let i = 0; i < collection.length; i++) {
    val = collection[i][property];
    index = values.indexOf(val);
    if (index > -1) {
      result[index].push(collection[i]);
    } else {
      values.push(val);
      result.push([collection[i]]);
    }
  }

  return result;
}

/**
 * @func compose
 * @summary composes functions, such that `f(g(args)) = f(args) • g(args)`
 *  - traditional function composition
 *  - despite not using `reduceRight` (as `async` below), the explicit composition order
 *    (e.g., `f(g..args))` does the same thing
 * @param  {function[]} fns functions to compose
 * @returns {function} composed function to take any `args`
 */
export const compose = (...fns) =>
  fns.reduce(
    (f, g) =>
      (...args) =>
        f(g(...args)),
  );

/**
 * @function composeAsync
 * @async
 * @summary compose asynchronous functions
 *  - create a new async function that's the result of `await g(await f(args))`, for f(args) • g(args)
 *  - functions are composed from right to left, so the last function passed to the chain will be executed first
 *  - output from one function is used as the input to the next
 * @param  {function[]} fns functions to compose
 * @returns {Promise<any>} promise with comoposed function result (must `await` or `.then()`)
 */
export const composeAsync =
  (...fns) =>
    (...args) =>
      fns.reduceRight(
        (chain, func) => chain.then(func),
        Promise.resolve(...args),
      );
